import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaComments,
  FaPaperPlane,
  FaRobot,
  FaTrashAlt,
  FaUser
} from "react-icons/fa";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

function useResidentSelection() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  return {
    patientId: String(location.state?.patientId || params.get("patient_id") || "").trim(),
    patientName: String(location.state?.patientName || params.get("patient_name") || "").trim(),
    preferredAgentSlug: String(
      location.state?.selectedAgentSlug || params.get("agent_slug") || ""
    ).trim()
  };
}

const WorkerAgentWorkspace = () => {
  const navigate = useNavigate();
  const { patientId, patientName, preferredAgentSlug } = useResidentSelection();
  const token = useMemo(() => localStorage.getItem("token"), []);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [selectedAgentSlug, setSelectedAgentSlug] = useState(preferredAgentSlug);
  const [threadId, setThreadId] = useState(null);
  const [chatThreads, setChatThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [chatError, setChatError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendStartedAt, setSendStartedAt] = useState(null);
  const [sendElapsedSeconds, setSendElapsedSeconds] = useState(0);
  const [threadToDelete, setThreadToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 980);
  const textareaRef = useRef(null);

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.slug === selectedAgentSlug) || null,
    [agents, selectedAgentSlug]
  );

  const goBackToResidentProfile = () => {
    if (!patientId) {
      navigate("/activatedpatients");
      return;
    }

    const params = new URLSearchParams();
    if (patientName) {
      params.set("patient_name", patientName);
    }
    if (selectedAgentSlug) {
      params.set("agent_slug", selectedAgentSlug);
    }

    navigate(
      `/worker/patients/${encodeURIComponent(patientId)}${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      {
        state: {
          patientId,
          patientName,
          selectedAgentSlug
        }
      }
    );
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 980);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isSending || !sendStartedAt) {
      setSendElapsedSeconds(0);
      return;
    }

    const tick = () => {
      setSendElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - sendStartedAt) / 1000))
      );
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isSending, sendStartedAt]);

  const fetchAgents = useCallback(async () => {
    if (!token) {
      setLoadingAgents(false);
      setChatError("Worker login required.");
      return;
    }

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.LIST}`,
        { headers: authHeaders }
      );
      const nextAgents = Array.isArray(res.data?.agents) ? res.data.agents : [];
      setAgents(nextAgents);
      setSelectedAgentSlug((currentSlug) => {
        if (currentSlug && nextAgents.some((agent) => agent.slug === currentSlug)) {
          return currentSlug;
        }
        if (preferredAgentSlug && nextAgents.some((agent) => agent.slug === preferredAgentSlug)) {
          return preferredAgentSlug;
        }
        return nextAgents[0]?.slug || "";
      });
      setChatError("");
    } catch (err) {
      setChatError(err.response?.data?.message || "Failed to load worker agents.");
    } finally {
      setLoadingAgents(false);
    }
  }, [authHeaders, preferredAgentSlug, token]);

  const fetchThreadList = useCallback(async () => {
    if (!selectedAgentSlug || !patientId) {
      setChatThreads([]);
      return;
    }

    setThreadsLoading(true);

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.CHAT_THREADS}`,
        {
          headers: authHeaders,
          params: {
            slug: selectedAgentSlug,
            patient_id: patientId
          }
        }
      );
      setChatThreads(Array.isArray(res.data?.sessions) ? res.data.sessions : []);
    } catch (err) {
      setChatError(err.response?.data?.message || "Failed to load worker chat threads.");
      setChatThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  }, [authHeaders, patientId, selectedAgentSlug]);

  const fetchHistory = useCallback(
    async (targetThreadId = null) => {
      if (!selectedAgentSlug || !patientId) {
        setMessages([]);
        setThreadId(null);
        return;
      }

      setHistoryLoading(true);

      try {
        const params = {
          slug: selectedAgentSlug,
          patient_id: patientId
        };
        if (targetThreadId) {
          params.thread_id = targetThreadId;
        }

        const res = await axios.get(
          `${config.API_BASE_URL}${config.WORKER_AGENTS.CHAT_HISTORY}`,
          { headers: authHeaders, params }
        );
        setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
        setThreadId(res.data?.session?.thread_id || null);
        setChatError("");
      } catch (err) {
        setMessages([]);
        setThreadId(null);
        setChatError(err.response?.data?.message || "Failed to load worker chat history.");
      } finally {
        setHistoryLoading(false);
      }
    },
    [authHeaders, patientId, selectedAgentSlug]
  );

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    if (!selectedAgentSlug || !patientId) return;

    let cancelled = false;

    const load = async () => {
      await fetchThreadList();
      if (cancelled) return;
      await fetchHistory(null);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchHistory, fetchThreadList, patientId, selectedAgentSlug]);

  const handleStartNewThread = () => {
    setThreadId(null);
    setMessages([]);
    setChatError("");
    setMessageInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleSendMessage = async () => {
    if (!selectedAgent || !patientId) return;

    const trimmedInput = messageInput.trim();
    if (!trimmedInput) {
      setChatError("Type a message before sending.");
      return;
    }

    const previousMessages = [...messages];
    const optimisticUserMessage = {
      id: `local-user-${Date.now()}`,
      role: "user",
      content: trimmedInput,
      created_at: new Date().toISOString()
    };
    const optimisticAssistantMessage = {
      id: `local-assistant-${Date.now()}`,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
      pending: true
    };

    setMessages((prev) => [...prev, optimisticUserMessage, optimisticAssistantMessage]);
    setMessageInput("");
    setIsSending(true);
    setSendStartedAt(Date.now());
    setChatError("");

    try {
      const payload = {
        slug: selectedAgent.slug,
        input: trimmedInput,
        patient_id: patientId
      };
      if (threadId) {
        payload.thread_id = threadId;
      }

      const res = await axios.post(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.CHAT_RUN}`,
        payload,
        { headers: authHeaders }
      );

      setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
      setThreadId(res.data?.thread_id || null);
      await fetchThreadList();
    } catch (err) {
      const errorMessage =
        err.response?.data?.provider_error ||
        err.response?.data?.message ||
        "Failed to run worker agent.";
      setMessages(previousMessages);
      setMessageInput(trimmedInput);
      setChatError(errorMessage);
    } finally {
      setIsSending(false);
      setSendStartedAt(null);
      setSendElapsedSeconds(0);
    }
  };

  const confirmDeleteThread = async () => {
    if (!threadToDelete) return;

    try {
      await axios.delete(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.DELETE_THREAD}`,
        {
          headers: authHeaders,
          params: {
            slug: selectedAgentSlug,
            patient_id: patientId,
            thread_id: threadToDelete
          }
        }
      );
      setChatThreads((prev) => prev.filter((item) => item.thread_id !== threadToDelete));
      if (threadId === threadToDelete) {
        handleStartNewThread();
      }
    } catch (err) {
      setChatError(err.response?.data?.message || "Failed to delete thread.");
    } finally {
      setThreadToDelete(null);
    }
  };

  const onComposerKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleInput = (event) => {
    setMessageInput(event.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const value = new Date(isoString);
    if (Number.isNaN(value.getTime())) return "";
    return value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const orderedThreads = Array.isArray(chatThreads) ? chatThreads : [];
  const canSend = Boolean(selectedAgent && patientId && messageInput.trim() && !isSending);

  return (
    <WorkerSidebarShell activeItem="patients">
      <div style={workspace}>
        <div style={topBar}>
          <button type="button" style={backButton} onClick={goBackToResidentProfile}>
            <FaArrowLeft /> Back To Care Profile
          </button>
        </div>

        <div style={hero}>
          <div style={residentCard}>
            <div style={residentMeta}>
              <div style={residentAvatar}>
                <FaUser />
              </div>
              <div>
                <div style={eyebrow}>Working In Elder Context</div>
                <h1 style={heroTitle}>{patientName || "Selected resident"}</h1>
                <p style={heroSubtitle}>
                  The agent stays scoped to this resident while you review profile,
                  medications, interactions, and other shared elder context.
                </p>
              </div>
            </div>
          </div>
        </div>

        {!patientId ? (
          <div style={emptyPanel}>
            <h2 style={emptyTitle}>No resident selected</h2>
            <p style={emptyCopy}>
              Open <strong>People I Support</strong>, expand a resident, and launch an
              agent from there.
            </p>
            <button type="button" style={secondaryAction} onClick={() => navigate("/activatedpatients")}>
              Go To People I Support
            </button>
          </div>
        ) : (
          <div style={{ ...chatLayout, ...(isMobile ? chatLayoutMobile : null) }}>
            <aside style={{ ...sidebar, ...(isMobile ? sidebarMobile : null) }}>
              <section style={panel}>
                <div style={panelHeader}>
                  <h2 style={panelTitle}>Available Agents</h2>
                  <span style={badge}>{agents.length}</span>
                </div>
                {loadingAgents ? <p style={helperText}>Loading agents...</p> : null}
                {!loadingAgents && agents.length === 0 ? (
                  <div style={emptyBlock}>
                    <p style={helperText}>No worker agents available yet.</p>
                    <button type="button" style={secondaryAction} onClick={() => navigate("/worker-agents")}>
                      Add Agents
                    </button>
                  </div>
                ) : null}
                <div style={agentList}>
                  {agents.map((agent) => {
                    const active = selectedAgentSlug === agent.slug;
                    return (
                      <button
                        key={agent.slug}
                        type="button"
                        style={{ ...agentButton, ...(active ? agentButtonActive : null) }}
                        onClick={() => setSelectedAgentSlug(agent.slug)}
                      >
                        <div style={agentButtonHeader}>
                          <FaRobot />
                          <span>{agent.name}</span>
                        </div>
                        <span style={agentButtonMeta}>
                          {agent.tools?.length || 0} tools · {agent.model || "No model"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section style={panel}>
                <div style={panelHeader}>
                  <h2 style={panelTitle}>Threads</h2>
                  <button type="button" style={secondaryAction} onClick={handleStartNewThread} disabled={isSending}>
                    New Thread
                  </button>
                </div>
                {threadsLoading ? <p style={helperText}>Loading threads...</p> : null}
                {!threadsLoading && orderedThreads.length === 0 ? (
                  <p style={helperText}>No prior threads for this resident and agent.</p>
                ) : null}
                <div style={threadList}>
                  {orderedThreads.map((session, index) => {
                    const active = threadId && session.thread_id === threadId;
                    return (
                      <div key={session.thread_id || index} style={{ ...threadItem, ...(active ? threadItemActive : null) }}>
                        <button
                          type="button"
                          style={threadSelect}
                          onClick={() => fetchHistory(session.thread_id)}
                          disabled={isSending}
                        >
                          <span style={threadTitle}>
                            {active ? "Current Thread" : `Thread ${index + 1}`}
                          </span>
                          <span style={threadMeta}>
                            {session.updated_at
                              ? new Date(session.updated_at).toLocaleString()
                              : "No timestamp"}
                          </span>
                        </button>
                        <button
                          type="button"
                          style={threadDelete}
                          onClick={() => setThreadToDelete(session.thread_id)}
                          disabled={isSending}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            </aside>

            <section style={{ ...chatPanel, ...(isMobile ? chatPanelMobile : null) }}>
              <div style={chatHeader}>
                <div>
                  <h2 style={chatTitle}>{selectedAgent?.name || "Select an agent"}</h2>
                  <p style={chatSubtitle}>
                    Keep the task resident-specific. Ask for profile, medications, or recent
                    interactions when you need context.
                  </p>
                </div>
                {isSending ? (
                  <div style={pendingPill}>
                    <FaComments />
                    <span>Working... {sendElapsedSeconds}s</span>
                  </div>
                ) : null}
              </div>

              <div style={messagesPanel}>
                {historyLoading ? <p style={helperText}>Loading chat history...</p> : null}
                {!historyLoading && messages.length === 0 ? (
                  <div style={emptyMessages}>
                    <p style={emptyTitle}>No messages yet.</p>
                    <p style={helperText}>
                      Try: "Summarize today’s medications and recent interactions for this resident."
                    </p>
                  </div>
                ) : null}

                {messages.map((message, index) => {
                  const isUser = message.role === "user";
                  const content = message.pending
                    ? `Working... ${sendElapsedSeconds}s elapsed`
                    : message.content;
                  return (
                    <div
                      key={message.id || `${message.role}-${index}-${message.created_at || "na"}`}
                      style={{ ...bubbleRow, justifyContent: isUser ? "flex-end" : "flex-start" }}
                    >
                      <div style={{ ...bubble, ...(isUser ? userBubble : assistantBubble) }}>
                        <div style={bubbleHeader}>
                          <span style={bubbleRole}>{isUser ? "You" : selectedAgent?.name || "Agent"}</span>
                          {message.created_at ? (
                            <span style={bubbleTime}>{formatTime(message.created_at)}</span>
                          ) : null}
                        </div>
                        <div style={bubbleText}>{content}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={composer}>
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={messageInput}
                  onChange={handleInput}
                  onKeyDown={onComposerKeyDown}
                  placeholder="Type a resident-specific task for the agent..."
                  style={composerInput}
                />
                <button
                  type="button"
                  style={{ ...sendButton, ...(canSend ? null : disabledSendButton) }}
                  onClick={handleSendMessage}
                  disabled={!canSend}
                >
                  <FaPaperPlane />
                </button>
              </div>

              {chatError ? <p style={errorText}>{chatError}</p> : null}
            </section>
          </div>
        )}

        {threadToDelete ? (
          <div style={modalOverlay}>
            <div style={modalCard}>
              <h3 style={modalTitle}>Delete Thread</h3>
              <p style={modalText}>
                Delete this worker thread for <strong>{patientName || "this resident"}</strong>?
              </p>
              <div style={modalActions}>
                <button type="button" style={modalCancelButton} onClick={() => setThreadToDelete(null)}>
                  Cancel
                </button>
                <button type="button" style={modalDeleteButton} onClick={confirmDeleteThread}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </WorkerSidebarShell>
  );
};

export default WorkerAgentWorkspace;

const workspace = {
  display: "grid",
  gap: "16px"
};

const topBar = {
  display: "flex",
  justifyContent: "flex-start"
};

const backButton = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#0E5580",
  borderRadius: "10px",
  padding: "10px 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  fontWeight: 700
};

const hero = {
  display: "grid"
};

const residentCard = {
  background: "linear-gradient(135deg, #ffffff 0%, #f0f8fd 100%)",
  border: "1px solid #deebf3",
  borderRadius: "18px",
  padding: "22px",
  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)"
};

const residentMeta = {
  display: "flex",
  alignItems: "flex-start",
  gap: "16px"
};

const residentAvatar = {
  width: "56px",
  height: "56px",
  borderRadius: "16px",
  background: "#0E5580",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  flexShrink: 0
};

const eyebrow = {
  color: "#0E5580",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  marginBottom: "8px"
};

const heroTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "30px"
};

const heroSubtitle = {
  margin: "10px 0 0",
  color: "#4f6475",
  lineHeight: 1.6,
  maxWidth: "760px"
};

const emptyPanel = {
  background: "#fff",
  border: "1px solid #deebf3",
  borderRadius: "16px",
  padding: "24px"
};

const emptyTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "20px"
};

const emptyCopy = {
  margin: "10px 0 0",
  color: "#4f6475",
  lineHeight: 1.6
};

const chatLayout = {
  display: "grid",
  gridTemplateColumns: "320px minmax(0, 1fr)",
  gap: "16px"
};

const chatLayoutMobile = {
  gridTemplateColumns: "1fr"
};

const sidebar = {
  display: "grid",
  gap: "16px",
  alignContent: "start"
};

const sidebarMobile = {
  order: 2
};

const panel = {
  background: "#fff",
  border: "1px solid #deebf3",
  borderRadius: "16px",
  padding: "18px",
  display: "grid",
  gap: "14px"
};

const panelHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px"
};

const panelTitle = {
  margin: 0,
  color: "#0E5580",
  fontSize: "18px"
};

const badge = {
  minWidth: "28px",
  height: "28px",
  borderRadius: "999px",
  background: "#edf7ff",
  color: "#0E5580",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "13px"
};

const helperText = {
  margin: 0,
  color: "#5d7182",
  lineHeight: 1.5
};

const emptyBlock = {
  display: "grid",
  gap: "12px"
};

const secondaryAction = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#0E5580",
  borderRadius: "10px",
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 700,
  justifySelf: "flex-start"
};

const agentList = {
  display: "grid",
  gap: "10px"
};

const agentButton = {
  border: "1px solid #deebf3",
  background: "#f9fcff",
  color: "#12344d",
  borderRadius: "12px",
  padding: "12px",
  textAlign: "left",
  cursor: "pointer",
  display: "grid",
  gap: "8px"
};

const agentButtonActive = {
  borderColor: "#0E5580",
  background: "#eef7fc",
  boxShadow: "0 10px 20px rgba(14, 85, 128, 0.08)"
};

const agentButtonHeader = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 700
};

const agentButtonMeta = {
  color: "#5d7182",
  fontSize: "12px"
};

const threadList = {
  display: "grid",
  gap: "10px"
};

const threadItem = {
  border: "1px solid #deebf3",
  borderRadius: "12px",
  background: "#f9fcff",
  display: "flex",
  alignItems: "stretch",
  overflow: "hidden"
};

const threadItemActive = {
  borderColor: "#0E5580",
  background: "#eef7fc"
};

const threadSelect = {
  flex: 1,
  border: "none",
  background: "transparent",
  textAlign: "left",
  padding: "12px",
  cursor: "pointer",
  display: "grid",
  gap: "4px"
};

const threadTitle = {
  color: "#12344d",
  fontWeight: 700,
  fontSize: "13px"
};

const threadMeta = {
  color: "#5d7182",
  fontSize: "12px"
};

const threadDelete = {
  width: "44px",
  border: "none",
  background: "transparent",
  color: "#b42318",
  cursor: "pointer"
};

const chatPanel = {
  background: "#fff",
  border: "1px solid #deebf3",
  borderRadius: "18px",
  padding: "18px",
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr) auto auto",
  gap: "14px",
  minHeight: "620px"
};

const chatPanelMobile = {
  minHeight: "520px",
  order: 1
};

const chatHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap"
};

const chatTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "24px"
};

const chatSubtitle = {
  margin: "8px 0 0",
  color: "#5d7182",
  lineHeight: 1.5,
  maxWidth: "640px"
};

const pendingPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  borderRadius: "999px",
  background: "#edf7ff",
  color: "#0E5580",
  padding: "8px 12px",
  fontSize: "13px",
  fontWeight: 700
};

const messagesPanel = {
  minHeight: 0,
  overflowY: "auto",
  border: "1px solid #deebf3",
  borderRadius: "14px",
  background: "#f9fcff",
  padding: "16px"
};

const emptyMessages = {
  textAlign: "center",
  paddingTop: "24px"
};

const bubbleRow = {
  display: "flex",
  marginBottom: "10px"
};

const bubble = {
  maxWidth: "78%",
  borderRadius: "14px",
  padding: "12px 14px"
};

const userBubble = {
  background: "#0E5580",
  color: "#fff"
};

const assistantBubble = {
  background: "#fff",
  border: "1px solid #d4e3ee",
  color: "#233d50"
};

const bubbleHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "4px"
};

const bubbleRole = {
  fontSize: "12px",
  fontWeight: 800
};

const bubbleTime = {
  fontSize: "11px",
  opacity: 0.74
};

const bubbleText = {
  whiteSpace: "pre-wrap",
  lineHeight: 1.55
};

const composer = {
  display: "flex",
  alignItems: "flex-end",
  gap: "12px",
  border: "1px solid #c7dae8",
  borderRadius: "24px",
  background: "#fff",
  padding: "8px 12px"
};

const composerInput = {
  flex: 1,
  border: "none",
  resize: "none",
  outline: "none",
  minHeight: "24px",
  maxHeight: "150px",
  padding: "10px 4px 10px 8px",
  fontSize: "15px",
  lineHeight: 1.5,
  color: "#12344d",
  boxSizing: "border-box"
};

const sendButton = {
  width: "42px",
  height: "42px",
  minWidth: "42px",
  borderRadius: "50%",
  border: "none",
  background: "#0E5580",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer"
};

const disabledSendButton = {
  opacity: 0.42,
  cursor: "not-allowed",
  background: "#9fb6c8"
};

const errorText = {
  margin: 0,
  color: "#b42318",
  fontWeight: 600
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.58)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  zIndex: 9999
};

const modalCard = {
  background: "#fff",
  borderRadius: "16px",
  padding: "24px",
  maxWidth: "420px",
  width: "100%",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.24)"
};

const modalTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "20px"
};

const modalText = {
  margin: "12px 0 0",
  color: "#4f6475",
  lineHeight: 1.5
};

const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "20px"
};

const modalCancelButton = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#4f6475",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 600
};

const modalDeleteButton = {
  border: "none",
  background: "#b42318",
  color: "#fff",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 700
};
